/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

// @mui material components
import React, { useState } from 'react'

import Card from '@mui/material/Card'
import { sendPasswordResetEmail } from 'firebase/auth'
import { useNavigate } from 'react-router-dom'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { auth } from 'config/firebase'
import { ROUTE_PATHS } from 'routes'

import BasicLayout from '../components/BasicLayout'
import ResetPasswordSuccessDialog from '../components/ResetPasswordSuccessDialog'

function ResetPassword() {
	const [emailInput, setEmailInput] = useState<string>('')
	const [errorMsg, setErrorMsg] = useState<string | null>(null)
	const [dialogOpen, setDialogOpen] = useState<boolean>(false)

	const navigate = useNavigate()

	const handleResetPasswordClick = () => {
		sendPasswordResetEmail(auth, emailInput.trim())
			.then(() => {
				console.log('Password reset email sent')
				setDialogOpen(true)
			})
			.catch(error => {
				console.error('Error sending password reset email:', error.code)
				if (error.code === 'auth/invalid-email' || error.code === 'auth/missing-email') {
					setErrorMsg(
						'Enter a valid username. Your username is in the format of an email address, such as username@company.com'
					)
				}
			})
	}

	return (
		<BasicLayout>
			<ResetPasswordSuccessDialog
				open={dialogOpen}
				email={emailInput.trim()}
				onClose={() => {
					navigate(ROUTE_PATHS.AUTHENTICATION.SIGN_IN, { replace: true })
					setDialogOpen(false)
				}}
			/>
			<Card>
				<MDBox
					variant="contained"
					bgColor="dark"
					borderRadius="lg"
					coloredShadow="success"
					mx={2}
					mt={-3}
					py={2}
					mb={1}
					textAlign="center"
				>
					<MDTypography variant="h3" fontWeight="medium" color="white" mt={1}>
						Reset Password
					</MDTypography>
				</MDBox>
				<MDBox pt={1} pb={3} px={3}>
					<MDTypography variant="body2">Having trouble logging in?</MDTypography>
					<MDBox component="ul" pl={2}>
						<li>
							<MDTypography variant="body2" component="span">
								Passwords are case-sensitive.
							</MDTypography>
						</li>
						<li>
							<MDTypography variant="body2" component="span">
								Make sure the user is registered.
							</MDTypography>
						</li>
					</MDBox>
					<MDTypography variant="body2">
						To reset the password please enter the email of the user.
					</MDTypography>
					<MDBox component="form" role="form">
						<MDBox mt={3} mb={4}>
							<MDInput
								type="email"
								label="Email"
								variant="outlined"
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailInput(e.target.value)}
								value={emailInput}
								InputLabelProps={{ shrink: true }}
								fullWidth
							/>
						</MDBox>

						{errorMsg && (
							<MDBox mt={1} mb={2}>
								<MDTypography variant="body1" color="error" style={{ fontSize: 14, lineHeight: 1 }}>
									{errorMsg}
								</MDTypography>
							</MDBox>
						)}
						<MDBox mt={3} mb={1}>
							<MDButton
								variant="contained"
								color="dark"
								onClick={handleResetPasswordClick}
								fullWidth
							>
								reset
							</MDButton>
						</MDBox>
						<MDBox>
							<MDButton
								variant="text"
								color="primary"
								onClick={() => navigate(ROUTE_PATHS.AUTHENTICATION.SIGN_IN, { replace: true })}
								fullWidth
							>
								Return to login
							</MDButton>
						</MDBox>
					</MDBox>
				</MDBox>
			</Card>
		</BasicLayout>
	)
}

export default ResetPassword
